
import React, { useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { deleteUser, getUsersByOrgId, updateUser } from "../Services/userService";
import "../Style/Common.css";
import Footer from "../Footer/Footer";
import { IUser, IUserRequest } from "../Interfaces/IUser";
import EditUser from "./EditUser";
import Swal from "sweetalert2";
import { Container, Box ,Button} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCustomerPopup from "../Sales/AddCustomerPopup";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
const CustomerList: React.FC = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchText, setSearchText] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);  // State for managing the edit modal
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null); // Selected user for editing
  const [userTypeToAdd, setUserTypeToAdd] = useState<
      "customer" | "vendor" | "salesPerson" | null
    >(null);


  const orgId = localStorage.getItem("orgId") || "";

  useEffect(() => {
    Swal.fire({
      title: "Loading",
      html: "Please wait while we load the users...",
      didOpen: () => {
        Swal.showLoading();
      },
    });
    const fetchUsers = async () => {
      try {
        const data = await getUsersByOrgId(orgId);
       const normalizedData = data.map((user: any) => ({
      ...user,
      createdAt: user.createdAt?.seconds
        ? user.createdAt.seconds * 1000   // Firestore timestamp → ms
        : new Date(user.createdAt).getTime() || 0,
    }));

    // ✅ Sort latest first
    normalizedData.sort((a: any, b: any) => b.createdAt - a.createdAt);
        setUsers(normalizedData);
        setFilteredUsers(normalizedData);
        
        Swal.close();
      } catch (error : any) {
        console.error("Failed to fetch users:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to fetch users",
        });
      }
    };
    fetchUsers();
  }, [orgId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const lowercasedSearchText = searchText.toLowerCase();
  
      const filtered = users.filter((user) =>
       (user.displayName ? user.displayName.toLowerCase() : "").includes(lowercasedSearchText) ||
        (user?.firstName ? user.firstName.toLowerCase() : "").includes(lowercasedSearchText) ||
        (user?.lastName ? user.lastName.toLowerCase() : "").includes(lowercasedSearchText) ||
        (user?.email ? user.email.toLowerCase() : "").includes(lowercasedSearchText) ||
        (user?.phone ? user.phone.includes(lowercasedSearchText) : false) ||
        (user?.userType ? user.userType.toLowerCase() : "").includes(lowercasedSearchText) ||
        (user?.companyName ? user.companyName.toLowerCase() : "").includes(lowercasedSearchText)||
        (user?.customerName ? user.customerName.toLowerCase() : "").includes(lowercasedSearchText)
      );
  
      setFilteredUsers(filtered);
    }, 300); // 300ms debounce
  
    return () => clearTimeout(timer);
  }, [searchText, users]);
  

  const handleDelete = async (customerId: string, orgId: string): Promise<void> => {
    const requestData: IUserRequest = {
      updatedBy: "sysAdmin",
      orgId: orgId,
      customerId: customerId,
    };

    if (window.confirm("Are you sure you want to delete this item?")) {
      const previousUsers = [...users];
      const previousFiltered = [...filteredUsers];

      setUsers((prevUsers) => prevUsers.filter((user) => user.Id !== customerId));
      setFilteredUsers((prevFiltered) =>
        prevFiltered.filter((user) => user.Id !== customerId)
      );

      try {
        await deleteUser(requestData);
        Swal.fire({
          icon: "success",
          title: "Item deleted successfully!",
          confirmButtonText: "Ok",
        });
      } catch (error: any) {
        setUsers(previousUsers);
        setFilteredUsers(previousFiltered);

        console.error("Failed to delete item:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to delete item",
          text: error.message || "An error occurred",
        });
      }
    }
  };

  const handleEdit = (user: IUser) => {
    setSelectedUser(user); // Set the user to edit
    setIsEditModalOpen(true); // Open the edit modal
  };

  const handleSaveEditedUser = async (updatedUser: IUser) => {
    try {
      // Update the user list with the edited data locally
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.Id === updatedUser.Id ? { ...user, ...updatedUser} : user
        )
      );
      setFilteredUsers((prevFiltered) =>
        prevFiltered.map((user) =>
          user.Id === updatedUser.Id ? { ...user, ...updatedUser } : user
        )
      );
  
      // Make an API call to update the user in the database
      await updateUser(updatedUser); // Pass the updated data to the API
  
      // Optionally, show a success message or perform any other actions after saving
      Swal.fire({
        icon: "success",
        title: "User Updated",
        text: "User updated successfully!",
      });
    } catch (error: any) {
      console.error("Failed to save edited user:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Save Changes",
        text: "Failed to save user changes. Please try again.",
      });
    }
  };
 const mapToIUser = (
  data: any,
  userType: "customer" | "vendor" | "salesPerson"
 ): IUser => {
  const firstName = data.firstName || "";
  const lastName = data.lastName || "";

  const displayName =
    data.displayName ||
    data.customerName ||
    data.companyName ||
    [firstName, lastName].filter(Boolean).join(" ") ||
    "Unknown";
    const now = Date.now();
    return {
    Id: data.customerId || data.Id || `${now}`,
    createdAt:now,
    salutation: data.salutation || "",
    firstName,
    lastName,
    customerName: displayName,
    displayName: displayName,
    email: data.email || "",
    phone: data.phone || "",
    companyName: data.companyName || "",
    GSTIN: data.GSTIN || "",
    website: data.website || "",
    address: {
      street: data?.address?.street ?? "",
      city: data?.address?.city ?? "",
      state: data?.address?.state ?? "",
      zipCode: data?.address?.zipCode ?? "",
      country: data?.address?.country ?? "",
    },
    userType,
    orgId: orgId,
  };
 };
  const columns: GridColDef[] = [
   {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
         <IconButton size="small" sx={{ padding: 0.5}}
           onClick={() => handleEdit(params.row)}
          ><EditIcon fontSize="small"  />
          </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
          <IconButton size="small" sx={{ padding: 0 }}
            color="error"
            onClick={() => handleDelete(params.row.Id, params.row.orgId)}         
          ><DeleteIcon  fontSize="small"/>
          </IconButton>
          </Tooltip>
        </>
      ),
    },
    { field: "customerName", headerName: "User Name", width: 200 },
    { field: "firstName", headerName: "First Name", width: 110 },
    { field: "lastName", headerName: "Last Name", width: 110 },
    // {
    //   field: "customerName",
    //   headerName: "User Name",
    //   width: 200,
    //   valueGetter: (params: { row: IUser }) => {
    //     const row = params?.row;
    //     return row?.userType == "customer"
    //       ? row?.customerName || "—"
    //       : `${row?.firstName || ""} ${row?.lastName || ""}`.trim();
    //   },
    // },
    { field: "GSTIN", headerName: "GST", width: 160 },
    { field: "phone", headerName: "Phone", width: 100 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "companyName", headerName: "Company", width: 120 },
    { field: "userType", headerName: "User Type", width: 110 },
  ];
  return (
    <>
     <div className="page-location">
        <span>Admin &gt; View Users</span>
      </div>
    <Container maxWidth="lg" sx={{ padding: 1 }}>
   <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
      gap: 1,
      flexWrap: "wrap",
    }}
    >
   <Box
    sx={{
      flex: "1 1 300px", 
      maxWidth: "500px",
      minWidth: "200px",
    }}
  >
    <input
      type="text"
      placeholder="🔍 Search by name, email, phone, or user type..."
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      style={{
        width: "100%",
        padding: "0.5rem 0.8rem",
        borderRadius: "20px",
        border: "1px solid #ccc",
        fontSize: "0.9rem",
        outline: "none",
        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
      }}
    />
  </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Add Customer">
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setUserTypeToAdd("customer")}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            px: 2,
            boxShadow: "none",
          }}
        >
          Customer
        </Button>
        </Tooltip>

         <Tooltip title="Add Vendor">
        <Button
          variant="contained"
          size="small"
          startIcon={<StorefrontIcon />}
          onClick={() => setUserTypeToAdd("vendor")}
          sx={{
            backgroundColor: "#9c27b0",
            textTransform: "none",
            borderRadius: "20px",
            px: 2,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#7b1fa2",
            },
          }}
        >
          Vendor
        </Button>
        </Tooltip>
        <Tooltip title="Add SalesPerson">
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<SupportAgentIcon />}
          onClick={() => setUserTypeToAdd("salesPerson")}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            px: 2,
            boxShadow: "none",
          }}
        >
          Sales
        </Button>
        </Tooltip>
      </Box>
    </Box>
      <div style={{ height: 600}}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={filteredUsers.length}
          getRowId={(row) => row.Id} 
          initialState={{
          sorting: {
             sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
          />
      </div>
      {/* Edit Modal */}
      {isEditModalOpen && selectedUser && (
        <EditUser
          type={selectedUser.userType as "customer" | "vendor" | "salesPerson"}
          orgId={orgId}
          onSave={(updatedData: any) => {
            handleSaveEditedUser(updatedData);
            setIsEditModalOpen(false);
          } }
          onClose={() => setIsEditModalOpen(false)}
          user={selectedUser} />
      )}
      {userTypeToAdd && (
        <AddCustomerPopup
          type={userTypeToAdd}
          orgId={orgId}
          onSave={(data) => {
            const mappedUser = mapToIUser(data, userTypeToAdd);

            setUsers((prev) => [mappedUser, ...prev]);
            setFilteredUsers((prev) => [mappedUser, ...prev]);

            setUserTypeToAdd(null);
          }}
          onClose={() => setUserTypeToAdd(null)}
        />
      )}

       {/* FOOTER */}
    <Box sx={{ mt: 5 }}>
      <Footer />
    </Box>
       </Container>
       </>
     
  );
};
export default CustomerList;

